import { useState, useEffect } from 'react'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { PasswordInput } from '@/components/password-input'
import { useAuth, useUser, useAuthStore } from '@/features/auth/'

const passwordSchema = z.string().min(6, '密码长度不能少于 6 位')
const otpSchema = z
  .string()
  .length(6, '请输入 6 位验证码')
  .regex(/^\d+$/, '验证码必须为数字')

export function ChangePasswordForm() {
  const { authVerifyCodeMutationAsync, authSendCodeMutationAsync } = useAuth()
  const { changePasswordAsync } = useUser()
  const { handleChangePassword, handleSendCode } = useAuthStore(
    (s) => s.handlers
  )
  const userEmail = useAuthStore((s) => s.auth.user?.email)
  const open = useAuthStore((s) => s.changePasswordDialog.open)
  const setOpen = useAuthStore((s) => s.changePasswordDialog.setOpen)

  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [otpError, setOtpError] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const onSendCode = async () => {
    if (!userEmail) return
    try {
      await handleSendCode(userEmail, authSendCodeMutationAsync)
      setCountdown(30)
    } catch {
      // 错误已在 store handler 中处理
    }
  }

  const onSubmit = async () => {
    if (!userEmail || !otp || !newPassword) return

    // Zod 验证 OTP
    const otpResult = otpSchema.safeParse(otp)
    if (!otpResult.success) {
      setOtpError(otpResult.error.issues[0].message)
      return
    }
    setOtpError('')

    // Zod 验证密码
    const passwordResult = passwordSchema.safeParse(newPassword)
    if (!passwordResult.success) {
      setPasswordError(passwordResult.error.issues[0].message)
      return
    }
    setPasswordError('')

    setLoading(true)
    try {
      await handleChangePassword(
        userEmail,
        otp,
        newPassword,
        authVerifyCodeMutationAsync,
        changePasswordAsync
      )
    } catch {
      // 错误已在 store handler 中处理
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant='outline'>更改密码</Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>更改密码</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          初次注册无密码，可直接验证邮箱后设置密码，以便下次直接通过邮箱+密码登录
        </DialogDescription>
        <div className='grid gap-6 py-4'>
          <div className='grid gap-2'>
            <Label htmlFor='otp'>验证码</Label>
            <div className='flex items-center gap-2'>
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(v) => {
                  setOtp(v)
                  if (otpError) setOtpError('')
                }}
                containerClassName='flex-1'
              >
                <InputOTPGroup className='w-full'>
                  <InputOTPSlot className='flex-1' index={0} />
                  <InputOTPSlot className='flex-1' index={1} />
                  <InputOTPSlot className='flex-1' index={2} />
                  <InputOTPSlot className='flex-1' index={3} />
                  <InputOTPSlot className='flex-1' index={4} />
                  <InputOTPSlot className='flex-1' index={5} />
                </InputOTPGroup>
              </InputOTP>
              <Button
                variant='outline'
                disabled={countdown > 0}
                onClick={onSendCode}
                className='w-[100px] shrink-0'
              >
                {countdown > 0 ? `${countdown}s` : '发送验证码'}
              </Button>
            </div>
            {otpError && <p className='text-destructive text-xs'>{otpError}</p>}
          </div>
          <div className='grid gap-2'>
            <Label htmlFor='new-password'>新密码</Label>
            <PasswordInput
              id='new-password'
              placeholder='请输入新密码'
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
                if (passwordError) setPasswordError('')
              }}
              className={passwordError ? 'border-destructive' : ''}
            />
            {passwordError && (
              <p className='text-destructive text-xs'>{passwordError}</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            type='submit'
            className='w-full'
            disabled={otp.length < 6 || !newPassword || loading}
            onClick={onSubmit}
          >
            {loading ? '正在修改...' : '修改'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
